/**
 * shippingConfig.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import { normalizeWeightThresholds, canadaCheapestStandard, volumetricBillableWeight } from './shippingCanadaPost';

/** Parse payment processing fee % from flat_rate_config (Stripe / PayPal pass-through). */
export function parseTransactionFeePct(flatRateConfig) {
  const pct = Number(flatRateConfig?.transaction_fee_pct);
  return Number.isFinite(pct) && pct > 0 ? pct : 0;
}

const debugShipping = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

let shippingSettingsPromise = null;
function fetchShippingSettingsCached() {
  if (!shippingSettingsPromise) {
    shippingSettingsPromise = fetch('/api/shipping-settings')
      .then((response) => (response.ok ? response.json() : null))
      .catch((error) => {
        shippingSettingsPromise = null;
        console.warn('Failed to fetch shipping settings from API, using hardcoded defaults:', error);
        return null;
      });
  }
  return shippingSettingsPromise;
}

/** Processing fee in cents on subtotal + shipping + tax + tariff (pre-fee total). */
export function calculateTransactionFeeCents(preFeeTotalCents, transactionFeePct) {
  const pct = Number(transactionFeePct) || 0;
  if (pct <= 0 || preFeeTotalCents <= 0) return 0;
  return Math.round(preFeeTotalCents * pct / 100);
}

function extractTransactionFeePctFromSettings(settings) {
  for (const row of settings || []) {
    if (!row?.flat_rate_config) continue;
    try {
      const cfg = typeof row.flat_rate_config === 'string'
        ? JSON.parse(row.flat_rate_config)
        : row.flat_rate_config;
      if (cfg && cfg.transaction_fee_pct != null) {
        return parseTransactionFeePct(cfg);
      }
    } catch (_) {
      /* ignore */
    }
  }
  return 0;
}

// Shipping configuration and calculation utilities
export const SHIPPING_METHODS = {
  STANDARD: {
    id: 'standard',
    name: 'Standard Shipping',
    description: '5-7 business days',
    baseRate: 5.99, // Base rate in USD
    freeThreshold: 75, // Free shipping threshold
    maxWeight: 50, // Max weight in lbs
    weightRate: 0.5, // Additional rate per lb over base weight
  },
  EXPEDITED: {
    id: 'expedited',
    name: 'Expedited Shipping',
    description: '2-3 business days',
    baseRate: 12.99,
    freeThreshold: 100,
    maxWeight: 50,
    weightRate: 0.75,
  },
  EXPRESS: {
    id: 'express',
    name: 'Express Shipping',
    description: '1-2 business days',
    baseRate: 19.99,
    freeThreshold: 150,
    maxWeight: 30,
    weightRate: 1.0,
  },
  INTERNATIONAL: {
    id: 'international',
    name: 'International Shipping',
    description: '7-14 business days',
    baseRate: 24.99,
    freeThreshold: 200,
    maxWeight: 50,
    weightRate: 1.5,
  }
};

// Country-specific shipping rates
export const COUNTRY_SHIPPING_RATES = {
  'US': {
    standard: 5.99,
    expedited: 12.99,
    express: 19.99,
    freeThreshold: 75
  },
  'CA': {
    standard: 8.99,
    expedited: 15.99,
    express: 24.99,
    freeThreshold: 50  // 🇨🇦 Free shipping within Canada on orders over $50
  },
  'UK': {
    standard: 12.99,
    expedited: 19.99,
    express: 29.99,
    freeThreshold: 125
  },
  'AU': {
    standard: 15.99,
    expedited: 22.99,
    express: 34.99,
    freeThreshold: 150
  },
  'DE': {
    standard: 11.99,
    expedited: 18.99,
    express: 27.99,
    freeThreshold: 120
  },
  'FR': {
    standard: 11.99,
    expedited: 18.99,
    express: 27.99,
    freeThreshold: 120
  },
  'JP': {
    standard: 14.99,
    expedited: 21.99,
    express: 32.99,
    freeThreshold: 140
  }
};

export function isDigitalAlbum(item) {
  if (!item) return false;
  return (
    item.is_digital_album === 1 ||
    item.is_digital_album === true ||
    item.is_digital_album === '1'
  );
}

/** Single product line (not a bundle container) that requires physical postage. */
export function isPhysicalProduct(item) {
  if (!item) return false;
  if (item.isCheckoutPromo) return false;
  if (isDigitalAlbum(item)) return false;
  if (item.shipping_enabled === false || item.shipping_enabled === 0) return false;
  return true;
}

/**
 * Expand cart lines into physical units for weight, flat-rate, and minimum shipping.
 * Bundle containers are replaced by their shippable constituents; digital parts are skipped.
 */
export function expandPhysicalShippingUnits(cartItems) {
  const units = [];

  for (const item of cartItems || []) {
    const qty = item.quantity || 1;
    const bundleParts = item.bundleProducts;

    if (item.isBundle && Array.isArray(bundleParts) && bundleParts.length > 0) {
      for (const part of bundleParts) {
        if (isPhysicalProduct(part)) {
          units.push({ ...part, quantity: qty });
        }
      }
    } else if (isPhysicalProduct(item)) {
      units.push(item);
    }
  }

  return units;
}

/**
 * Whether a cart line contributes to shipping (weight, flat-rate handling/per-item, product minimums).
 * Excludes digital albums, virtual items, and checkout promotional gifts ($0).
 * Bundles are billable only when at least one selected product needs postage.
 */
export function isBillableForShipping(item) {
  if (!item) return false;
  if (item.isCheckoutPromo) return false;

  if (item.isBundle && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0) {
    return item.bundleProducts.some(isPhysicalProduct);
  }

  return isPhysicalProduct(item);
}

function billableCartItems(cartItems) {
  return (cartItems || []).filter(isBillableForShipping);
}

/** Book vs CD for flat-rate buckets (handling + per-item tiers). */
export function classifyFlatRateItemType(item) {
  if (!item) return 'Book';

  const itemType = (item.item_type || '').toString().trim().toLowerCase();
  const category = (item.category || '').toString().trim().toLowerCase();
  const type = (item.type || '').toString().trim().toLowerCase();
  const name = (item.name || '').toString().trim().toLowerCase();

  if (/^(cd|recording|music|dvd|video)/i.test(itemType)) return 'CD';
  if (/\b(cd|dvd|video|audio|recording|music)\b/i.test(category)) return 'CD';
  if (/\b(cd|dvd)\b/i.test(type)) return 'CD';
  if (/\(cd\)|\bcd\b|\bdvd\b/i.test(name)) return 'CD';

  return 'Book';
}

/**
 * Count Book/CD units for flat-rate shipping, expanding bundle line items into
 * their constituent products so CDs inside bundles are billed correctly.
 * Digital / virtual bundle parts are excluded.
 */
export function countFlatRateBuckets(cartItems) {
  const buckets = {};

  const addUnits = (item, qty) => {
    const type = classifyFlatRateItemType(item);
    buckets[type] = (buckets[type] || 0) + qty;
  };

  for (const item of expandPhysicalShippingUnits(cartItems)) {
    addUnits(item, item.quantity || 1);
  }

  return buckets;
}

/**
 * Compute tiered per-item cost. Items fill tiers cumulatively:
 * e.g. tiers=[{up_to:2, per_item_cents:300},{up_to:5, per_item_cents:600},{up_to:null, per_item_cents:500}]
 * → items 1-2 @ $3, items 3-5 @ $6, items 6+ @ $5.
 * Falls back to flat per_item_cents when no tiers array exists.
 */
function computeTieredPerItem(qty, rates) {
  const tiers = rates.tiers;
  if (!tiers || !Array.isArray(tiers) || tiers.length === 0) {
    const cents = rates.per_item_cents || 0;
    return { totalCents: cents * qty, tierBreakdown: [{ from: 1, to: qty, perItemCents: cents, count: qty, subtotal: cents * qty }] };
  }

  const sorted = [...tiers].sort((a, b) => (a.up_to ?? Infinity) - (b.up_to ?? Infinity));
  let remaining = qty;
  let prevCap = 0;
  let totalCents = 0;
  const tierBreakdown = [];

  for (const tier of sorted) {
    if (remaining <= 0) break;
    const cap = tier.up_to ?? Infinity;
    const slotSize = cap - prevCap;
    const count = Math.min(remaining, slotSize);
    const subtotal = count * (tier.per_item_cents || 0);
    totalCents += subtotal;
    tierBreakdown.push({ from: prevCap + 1, to: prevCap + count, perItemCents: tier.per_item_cents || 0, count, subtotal });
    remaining -= count;
    prevCap = cap;
  }

  if (remaining > 0) {
    const lastRate = sorted[sorted.length - 1].per_item_cents || 0;
    const subtotal = remaining * lastRate;
    totalCents += subtotal;
    tierBreakdown.push({ from: qty - remaining + 1, to: qty, perItemCents: lastRate, count: remaining, subtotal });
  }

  return { totalCents, tierBreakdown };
}

/**
 * Flat-rate shipping: base shipping & handling (highest of applicable types) + a tiered
 * per-item postage adjustment that tracks postal weight bands.
 * Supports quantity tiers so per-item cost rises for mid-range orders to track weight-band costs.
 */
export function calculateFlatRateShipping(cartItems, country, flatConfig) {
  if (!flatConfig?.enabled) return null;

  const destKey = (country === 'CA') ? 'CA' : (country === 'US') ? 'US' : 'INTL';
  const destRates = flatConfig.destinations?.[destKey];
  if (!destRates) return null;

  const buckets = countFlatRateBuckets(cartItems);

  if (Object.keys(buckets).length === 0) return null;

  let maxHandlingCents = 0;
  let maxHandlingType = '';
  const perItemBreakdown = [];

  for (const [type, qty] of Object.entries(buckets)) {
    const rates = destRates[type] || destRates['Book'] || { handling_cents: 0, per_item_cents: 0 };
    const handling = rates.handling_cents || 0;
    if (handling > maxHandlingCents) {
      maxHandlingCents = handling;
      maxHandlingType = type;
    }
    const { totalCents, tierBreakdown } = computeTieredPerItem(qty, rates);
    perItemBreakdown.push({ type, qty, totalCents, tierBreakdown });
  }

  const perItemTotal = perItemBreakdown.reduce((sum, b) => sum + b.totalCents, 0);
  let baseCost = maxHandlingCents + perItemTotal;

  const fuelPct = Number(flatConfig.fuel_surcharge_pct) || 0;
  let fuelCents = 0;
  if (fuelPct > 0) {
    fuelCents = Math.round(baseCost * fuelPct / 100);
  }

  const totalCost = baseCost + fuelCents;

  const itemSummary = perItemBreakdown.map(b => `${b.qty}× ${b.type}`).join(' + ');
  const label = `Flat Rate (${destKey}: ${itemSummary})`;

  return {
    cost: totalCost,
    baseCents: baseCost,
    fuelCents,
    label,
    handlingType: maxHandlingType,
    handlingCents: maxHandlingCents,
    perItemBreakdown,
    destKey
  };
}

// Calculate shipping cost based on cart total, weight, and destination
export const calculateShipping = async (cartTotal, totalWeight, country = 'US', method = 'standard', cartItems = []) => {
  // Try to fetch rates from API first
  let countryRates = COUNTRY_SHIPPING_RATES[country] || COUNTRY_SHIPPING_RATES['US'];
  let usedApiRates = false;
  let weightThresholds = [];

  let flatRateConfig = null;
  let transactionFeePct = 0;

  try {
    const settings = await fetchShippingSettingsCached();
    if (settings) {
      transactionFeePct = extractTransactionFeePctFromSettings(settings);
      const countrySetting = settings.find(s => s.country_code === country);
      if (countrySetting && countrySetting.enabled) {
        countryRates = {
          standard: countrySetting.standard_rate,
          expedited: countrySetting.expedited_rate,
          express: countrySetting.express_rate,
          freeThreshold: countrySetting.free_threshold
        };
        usedApiRates = true;

        // Parse weight thresholds if they exist (array or Canada Post extended JSON)
        if (countrySetting.weight_thresholds) {
          try {
            weightThresholds = typeof countrySetting.weight_thresholds === 'string'
              ? JSON.parse(countrySetting.weight_thresholds)
              : countrySetting.weight_thresholds;
          } catch (e) {
            console.warn('Failed to parse weight_thresholds:', e);
            weightThresholds = [];
          }
        }

        // Parse flat-rate config if it exists
        if (countrySetting.flat_rate_config) {
          try {
            flatRateConfig = typeof countrySetting.flat_rate_config === 'string'
              ? JSON.parse(countrySetting.flat_rate_config)
              : countrySetting.flat_rate_config;
          } catch (e) {
            console.warn('Failed to parse flat_rate_config:', e);
          }
        }
      }
    }
  } catch (error) {
    console.warn('Failed to fetch shipping settings from API, using hardcoded defaults:', error);
  }

  const shippingMethod = SHIPPING_METHODS[method.toUpperCase()] || SHIPPING_METHODS.STANDARD;

  const shippableItems = billableCartItems(cartItems);

  // Digital items, checkout promos, and lines with shipping disabled → no postage
  if (shippableItems.length === 0) {
    return {
      cost: 0,
      method: shippingMethod,
      isFree: true,
      message: cartItems.length > 0
        ? 'No shipping required for digital or promotional items in your cart'
        : 'No items in cart',
      isWeightBased: false,
      usedApiRates,
      transactionFeePct,
    };
  }

  // Check if order qualifies for free shipping
  if (cartTotal >= (countryRates.freeThreshold * 100)) {
    return {
      cost: 0,
      method: shippingMethod,
      isFree: true,
      message: `Free shipping on orders over $${countryRates.freeThreshold}`,
      usedApiRates,
      transactionFeePct,
    };
  }

  // ── Flat-rate shipping (when enabled, takes precedence over weight-based) ──
  if (flatRateConfig?.enabled) {
    const flatResult = calculateFlatRateShipping(shippableItems, country, flatRateConfig);
    if (flatResult) {
      debugShipping('📦 Flat-rate Shipping:', flatResult);
      return {
        cost: flatResult.cost,
        method: shippingMethod,
        isFree: false,
        message: 'Flat-rate shipping estimate — any overcharge will be refunded',
        isWeightBased: false,
        isFlatRate: true,
        totalWeight: totalWeight,
        shippingBandLabel: flatResult.label,
        fuelBreakdown: flatResult.fuelCents > 0 ? { baseCents: flatResult.baseCents, fuelCents: flatResult.fuelCents } : null,
        flatRateBreakdown: {
          handlingType: flatResult.handlingType,
          handlingCents: flatResult.handlingCents,
          perItemBreakdown: flatResult.perItemBreakdown,
          destKey: flatResult.destKey
        },
        usedApiRates,
        transactionFeePct,
      };
    }
  }

  // totalWeight is already in kg from calculateCartWeight function
  const totalWeightKg = totalWeight;

  // Check weight-based thresholds (legacy arrays) or Canada domestic (lettermail vs flat-rate vs parcel)
  let weightBasedCost = null;
  let shippingBandLabel = null; // e.g. "2.0 kg parcel band", "Flat-Rate Box (Small)", "Lettermail 0.3 kg"
  let fuelBreakdown = null; // { baseCents, fuelCents } when fuel surcharge is applied
  const { bands, canadaExtended } = normalizeWeightThresholds(weightThresholds);

  // Determine longest-side constraint for CA flat-rate box eligibility
  let caRequiredLongestCm = 0;
  if (country === 'CA' && canadaExtended?.ca_shipment_longest_cm != null) {
    caRequiredLongestCm = Math.max(caRequiredLongestCm, Number(canadaExtended.ca_shipment_longest_cm) || 0);
  }
  if (country === 'CA' && shippableItems.length > 0) {
    const fromItems = Math.max(
      0,
      ...shippableItems.map((i) => Number(i.shipping_longest_cm) || 0)
    );
    caRequiredLongestCm = Math.max(caRequiredLongestCm, fromItems);
  }

  // ── Canada domestic: use canadaCheapestStandard which considers
  //    lettermail, parcel bands, flat-rate boxes, and regular parcel fallback.
  //    This covers ALL weight ranges from 0.1kg to 10kg+.
  if (country === 'CA' && canadaExtended) {
    if (method === 'standard' || method === 'expedited') {
      const result = canadaCheapestStandard(
        totalWeightKg,
        canadaExtended,
        countryRates.standard,
        caRequiredLongestCm
      );
      weightBasedCost = result.cost;
      shippingBandLabel = result.label;
      if (result.fuelCents > 0) fuelBreakdown = { baseCents: result.baseCents, fuelCents: result.fuelCents };
    } else if (method === 'express') {
      const xp = canadaExtended.xpresspost_1kg_cents;
      if (xp && totalWeightKg > 0 && totalWeightKg <= 1) {
        weightBasedCost = xp;
        shippingBandLabel = 'Xpresspost (≤ 1 kg)';
      } else {
        const result = canadaCheapestStandard(
          totalWeightKg,
          canadaExtended,
          countryRates.express,
          caRequiredLongestCm
        );
        weightBasedCost = result.cost;
        shippingBandLabel = result.label;
        if (result.fuelCents > 0) fuelBreakdown = { baseCents: result.baseCents, fuelCents: result.fuelCents };
      }
    }
  } else if (bands.length > 0) {
    // US / INTL / legacy arrays: simple weight-band lookup
    const sortedThresholds = [...bands].sort((a, b) => a.weight_kg - b.weight_kg);
    const applicableThreshold = sortedThresholds.find((t) => t.weight_kg >= totalWeightKg);
    if (applicableThreshold) {
      weightBasedCost = applicableThreshold.price_cents;
      shippingBandLabel = `${applicableThreshold.weight_kg} kg band`;
    } else {
      const lastBand = sortedThresholds[sortedThresholds.length - 1];
      weightBasedCost = lastBand.price_cents;
      shippingBandLabel = `${lastBand.weight_kg} kg band (max)`;
    }
  }

  // Calculate base shipping cost
  let baseCost = countryRates[method] || countryRates.standard;
  const originalBaseCost = baseCost;

  // Legacy per-lb fallback — only when no weight bands AND no Canada extended data
  if (bands.length === 0 && !canadaExtended) {
    const totalWeightLbs = totalWeight * 2.20462;
    if (totalWeightLbs > shippingMethod.maxWeight) {
      const excessWeight = totalWeightLbs - shippingMethod.maxWeight;
      baseCost += excessWeight * shippingMethod.weightRate;
    }
  }

  const weightAffectedCost = baseCost > originalBaseCost;

  // Check for product-specific minimum shipping costs (physical units only)
  const physicalUnits = expandPhysicalShippingUnits(cartItems);
  let minimumShippingCents = 0;
  if (physicalUnits.length > 0) {
    minimumShippingCents = Math.max(...physicalUnits.map(item => {
      // Use international minimum if applicable
      if (country !== 'CA' && country !== 'US') {
        return item.minimum_shipping_international_cents || item.minimum_shipping_cents || 0;
      }
      return item.minimum_shipping_cents || 0;
    }));
  }

  // Calculate standard shipping cost (from admin settings or weight-based)
  // This is the baseline shipping cost for this destination
  const standardShippingCents = weightBasedCost !== null ? weightBasedCost : Math.round(baseCost * 100);

  // ALWAYS use the HIGHER of:
  // 1. Standard shipping cost (from admin shipping settings)
  // 2. Product-specific minimum shipping cost
  // This ensures that if a product requires special shipping, we charge appropriately,
  // but we never charge less than the standard rate for the destination
  const finalCost = Math.max(standardShippingCents, minimumShippingCents);

  debugShipping('💰 Shipping Calculation:', {
    country,
    method,
    totalWeightKg,
    weightThresholdsConfigured: bands.length > 0 || !!canadaExtended,
    hasCaParcelBands: !!(canadaExtended?.parcel_bands?.length),
    weightBasedCost,
    weightAffectedCost,
    standardShippingCents,
    minimumShippingCents,
    finalCost,
    source: minimumShippingCents > standardShippingCents ? 'Product Minimum' :
      (weightBasedCost !== null ? (canadaExtended ? 'CA Domestic (lettermail/parcel/flatbox)' : 'Weight Band') :
        (weightAffectedCost ? 'Legacy Weight Calc' : 'Standard Rate'))
  });

  let message = null;
  let isWeightBased = false;

  // Determine which cost is being used and set appropriate flags
  if (finalCost === minimumShippingCents && minimumShippingCents > 0) {
    // Using product-specific minimum shipping
    message = 'Minimum shipping cost applied for this product';
    isWeightBased = false;
  } else if (weightBasedCost !== null && finalCost === weightBasedCost) {
    // Using weight-based threshold
    message = 'Weight-based shipping charge applied';
    isWeightBased = true;
  } else if (weightAffectedCost && finalCost === standardShippingCents) {
    // Using standard rate but weight affected the cost (legacy calculation)
    message = 'Weight-based shipping charge applied';
    isWeightBased = true;
  } else {
    // Using standard flat rate
    isWeightBased = false;
  }

  return {
    cost: finalCost,
    method: shippingMethod,
    isFree: false,
    message: message,
    isWeightBased: isWeightBased,
    totalWeight: totalWeightKg,
    shippingBandLabel: shippingBandLabel,
    fuelBreakdown: fuelBreakdown,
    usedApiRates,
    transactionFeePct,
  };
};

// Get available shipping methods for a country
export const getAvailableShippingMethods = (country = 'US') => {
  const countryRates = COUNTRY_SHIPPING_RATES[country] || COUNTRY_SHIPPING_RATES['US'];

  return Object.keys(SHIPPING_METHODS).map(key => {
    const method = SHIPPING_METHODS[key];
    const rate = countryRates[method.id] || method.baseRate;

    return {
      ...method,
      rate: Math.round(rate * 100), // Convert to cents
      freeThreshold: countryRates.freeThreshold
    };
  });
};

// Calculate total weight of cart items.
// Uses volumetric weight (L×W×H/6000) when product dimensions are available,
// billing the greater of actual vs volumetric per Canada Post rules.
// Bundle lines expand into physical constituents; digital parts are excluded.
export const calculateCartWeight = (items) => {
  const physicalUnits = expandPhysicalShippingUnits(items);
  debugShipping('🔢 Calculating cart weight for', physicalUnits.length, 'physical unit(s):');

  return physicalUnits.reduce((totalWeight, item) => {
    if ((item.minimum_shipping_cents > 0 || item.minimum_shipping_international_cents > 0) && (!item.weight_kg || item.weight_kg === 0)) {
      debugShipping(`  📦 Item "${item.name}" - Has minimum shipping (${item.minimum_shipping_cents}¢) but no weight, weight: 0kg`);
      return totalWeight;
    }

    const actualWeight = (item.weight_kg && item.weight_kg > 0) ? item.weight_kg : 0.2;

    // Check for volumetric weight if product dimensions are available
    const l = Number(item.shipping_length_cm) || 0;
    const w = Number(item.shipping_width_cm) || 0;
    const h = Number(item.shipping_height_cm) || 0;
    let itemWeight = actualWeight;
    if (l > 0 && w > 0 && h > 0) {
      const { billableKg, wasVolumetric } = volumetricBillableWeight(actualWeight, l, w, h);
      itemWeight = billableKg;
      if (wasVolumetric) {
        debugShipping(`  📐 Item "${item.name}" - Volumetric weight used: ${billableKg}kg (actual: ${actualWeight}kg, dims: ${l}×${w}×${h}cm)`);
      }
    }

    const qty = item.quantity || 1;
    const totalItemWeight = itemWeight * qty;
    debugShipping(`  ✅ Item "${item.name}" - weight: ${itemWeight}kg, qty: ${qty}, total: ${totalItemWeight}kg`);
    return totalWeight + totalItemWeight;
  }, 0);
};

// Convert weight from kg to lbs for shipping calculation
export const kgToLbs = (kg) => kg * 2.20462;
